/**
 * PasswordGate — simple client-side password screen.
 *
 * Uses the Web Crypto API (SubtleCrypto) to SHA-256 hash the entered password
 * and compare it against the baked-in hash. Unlock state is persisted in
 * sessionStorage so a page refresh doesn't force re-entry.
 *
 * Note: this is a UX barrier, not a security control. Determined users can
 * extract the hash from the bundle. Add ALB-level auth for real access control.
 */

import { useState } from 'react';
import { BRAND } from '../../brand';
import { BrandMark } from '../BrandMark/BrandMark';

/** SHA-256 hex of the access password. */
const EXPECTED_HASH = '0ad1d1a41490bef55f891512d10414b33e95c289bb16f227ede20f9ec7fe43db';

const SESSION_KEY = 'tcc_unlocked';

async function sha256hex(text: string): Promise<string> {
	const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
	return Array.from(new Uint8Array(buf))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
}

export function isUnlocked(): boolean {
	try {
		return sessionStorage.getItem(SESSION_KEY) === '1';
	} catch {
		return false;
	}
}

export function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
	const [value, setValue] = useState('');
	const [error, setError] = useState(false);
	const [checking, setChecking] = useState(false);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setChecking(true);
		setError(false);
		const hash = await sha256hex(value);
		if (hash === EXPECTED_HASH) {
			try {
				sessionStorage.setItem(SESSION_KEY, '1');
			} catch {
				/* ignore */
			}
			onUnlock();
		} else {
			setError(true);
			setValue('');
		}
		setChecking(false);
	}

	return (
		<div className="pg-screen">
			<div className="pg-card">
				{BRAND.logo ? (
					<h2 className="pg-title">
						<BrandMark />
						AI Token Cost Estimator
					</h2>
				) : (
					<>
						<div className="pg-badge">AI Token Cost Estimator</div>
						<h2 className="pg-title">{BRAND.name}</h2>
					</>
				)}
				<p className="pg-sub">Enter the access password to continue</p>

				<form onSubmit={handleSubmit}>
					<label htmlFor="password-input" className="sr-only">
						Password
					</label>
					<input
						id="password-input"
						type="password"
						placeholder="Password"
						value={value}
						aria-describedby={error ? 'password-error' : undefined}
						aria-invalid={error}
						className={`pg-input${error ? ' pg-input--error' : ''}`}
						onChange={(e) => {
							setValue(e.target.value);
							setError(false);
						}}
					/>
					{error && (
						<div id="password-error" role="alert" className="pg-error-msg">
							Incorrect password. Please try again.
						</div>
					)}
					<button type="submit" disabled={checking || !value} className="pg-btn">
						{checking ? 'Checking…' : 'Unlock'}
					</button>
				</form>
			</div>
		</div>
	);
}
